/**
 * ==========================================
 * LIVE SESSIONS API SERVICE
 * ==========================================
 * File: frontend/src/modules/session/services/live_sessions_api.ts
 *
 * API service for live session management including session lifecycle,
 * joining, navigation, and participant management.
 */

import { apiClient, API_BASE_URL, API_VERSION, addParticipantIdHeader } from '../../../core/config/api';
import axios from 'axios';
import type {
  LiveSessionCreate,
  LiveSessionResponse,
  JoinCodeLookup,
  JoinSessionRequest,
  JoinSessionResponse,
  NavigateRequest,
  NavigateResponse,
  TimelineNavigateRequest,
  StartSessionResponse,
  EndSessionRequest,
  EndSessionResponse,
  PauseResumeResponse,
  SessionProgress,
  ParticipantListResponse,
  HeartbeatRequest,
  HeartbeatResponse,
  PusherConfig,
  ClassContextPreviewResponse
} from '../types/live_session';

// ============================================
// ANONYMOUS API CLIENT (for join flow)
// ============================================

/**
 * Create a separate axios instance for anonymous join requests
 * that doesn't require authentication and won't redirect to login on 401
 */
const anonymousClient = axios.create({
  baseURL: `${API_BASE_URL}/api/${API_VERSION}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token if available (for logged-in users joining)
anonymousClient.interceptors.request.use(
  (config) => {
    // Try to get auth token from apiClient's default headers
    const authHeader = apiClient.defaults.headers.common['Authorization'];
    if (authHeader && typeof authHeader === 'string') {
      config.headers['Authorization'] = authHeader;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor that doesn't redirect on 401 (allows anonymous access)
anonymousClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Just reject without redirecting - let the component handle errors
    return Promise.reject(error);
  }
);

// ============================================
// API SERVICE IMPLEMENTATION
// ============================================

export const liveSessionsApi = {
  // ============================================
  // SESSION CREATION AND RETRIEVAL
  // ============================================

  /**
   * Create a new live session.
   *
   * The backend lifts ``content_type`` and ``content_id`` into the URL so
   * the access dependency gates ``<content_type>:publish`` on the
   * resource; this client splits them out of the existing payload shape
   * to keep callers unchanged.
   */
  createSession: async (data: LiveSessionCreate): Promise<LiveSessionResponse> => {
    const { content_type, content_id, ...body } = data;
    const response = await apiClient.post(
      `/live-sessions/new/${content_type}/${content_id}`,
      body
    );
    return response.data;
  },

  /**
   * Get live session details by ID for the educator/host surface.
   *
   * Backed by ``GET /studio/live-sessions/{session_id}`` — the host route
   * gated by ``require_access`` on the underlying activity/lesson grant.
   */
  getHostSession: async (sessionId: string): Promise<LiveSessionResponse> => {
    const response = await apiClient.get(`/studio/live-sessions/${sessionId}`);
    return response.data;
  },

  /**
   * Get live session details by ID for a joined participant.
   *
   * Backed by ``GET /delivery/live-sessions/{session_id}`` — the
   * participant route gated by an X-Participant-ID header validated
   * against the session-credential resolver. The participantId is
   * required (anonymous and authenticated participants both supply it).
   */
  getParticipantSession: async (
    sessionId: string,
    participantId: string,
  ): Promise<LiveSessionResponse> => {
    const config = addParticipantIdHeader({}, participantId);
    const response = await apiClient.get(
      `/delivery/live-sessions/${sessionId}`,
      config,
    );
    return response.data;
  },

  /**
   * @deprecated Use getClassContext() instead, which returns active_live_session info
   * along with assignment and student session data in a single call.
   *
   * Get educator's active/paused/lobby sessions
   * Used to check for existing sessions before creating a new one
   */
  getEducatorActiveSessions: async (params?: {
    activityId?: string;
    lessonId?: string;
    classId?: string;  // Use 'none' to filter for sessions without a class
    status?: string[];
  }): Promise<LiveSessionResponse[]> => {
    const searchParams = new URLSearchParams();
    if (params?.activityId) {
      searchParams.append('activity_id', params.activityId);
    }
    if (params?.lessonId) {
      searchParams.append('lesson_id', params.lessonId);
    }
    if (params?.classId) {
      searchParams.append('class_id', params.classId);
    }
    if (params?.status) {
      params.status.forEach(s => searchParams.append('status', s));
    }
    const queryString = searchParams.toString();
    const url = `/live-sessions/educator/active${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  // ============================================
  // SESSION LIFECYCLE
  // ============================================

  /**
   * Start a live session (transition from lobby to active)
   */
  startSession: async (sessionId: string): Promise<StartSessionResponse> => {
    const response = await apiClient.post(`/live-sessions/${sessionId}/start`);
    return response.data;
  },

  /**
   * Pause an active live session
   */
  pauseSession: async (sessionId: string): Promise<PauseResumeResponse> => {
    const response = await apiClient.post(`/live-sessions/${sessionId}/pause`);
    return response.data;
  },

  /**
   * Resume a paused live session
   */
  resumeSession: async (sessionId: string): Promise<PauseResumeResponse> => {
    const response = await apiClient.post(`/live-sessions/${sessionId}/resume`);
    return response.data;
  },

  /**
   * End a live session with optional async completion
   */
  endSession: async (
    sessionId: string,
    data: EndSessionRequest
  ): Promise<EndSessionResponse> => {
    const response = await apiClient.post(`/live-sessions/${sessionId}/end`, data);
    return response.data;
  },

  // ============================================
  // NAVIGATION (Whole Class Mode)
  // ============================================

  /**
   * Navigate to a slide (educator action for whole class mode)
   */
  navigate: async (
    sessionId: string,
    data: NavigateRequest
  ): Promise<NavigateResponse> => {
    const response = await apiClient.post(`/live-sessions/${sessionId}/navigate`, data);
    return response.data;
  },

  /**
   * Navigate timeline event within a slide (educator action for whole class presentations)
   * Broadcasts timeline_event:changed to all students via Pusher
   */
  navigateTimeline: async (
    sessionId: string,
    data: TimelineNavigateRequest
  ): Promise<{ slide_id: string; timeline_event: number }> => {
    const response = await apiClient.post(`/live-sessions/${sessionId}/navigate-timeline`, data);
    return response.data;
  },

  // ============================================
  // JOIN FLOW (Anonymous-friendly)
  // ============================================

  /**
   * Lookup a session by join code (no auth required)
   * Uses anonymousClient to allow unauthenticated access
   * If sessionToken is provided, sends it as query param for anonymous session resume
   */
  lookupByCode: async (joinCode: string, sessionToken?: string): Promise<JoinCodeLookup> => {
    const params: Record<string, string> = {};
    if (sessionToken) {
      params.session_token = sessionToken;
    }
    const response = await anonymousClient.get(`/live-sessions/join/${joinCode}`, { params });
    return response.data;
  },

  /**
   * Join a live session using join code (optional auth)
   * Uses anonymousClient to support both authenticated and anonymous users
   * If sessionToken is provided, sends it as query param to resume existing assignment progress
   */
  joinSession: async (
    joinCode: string,
    data: JoinSessionRequest,
    sessionToken?: string
  ): Promise<JoinSessionResponse> => {
    const params: Record<string, string> = {};
    if (sessionToken) {
      params.session_token = sessionToken;
    }
    const response = await anonymousClient.post(`/live-sessions/join/${joinCode}`, data, { params, withCredentials: true });
    return response.data;
  },

  // ============================================
  // PARTICIPANTS
  // ============================================

  /**
   * Get all participants in a live session for the educator/host surface.
   *
   * Backed by ``GET /studio/live-sessions/{session_id}/participants``.
   */
  getHostParticipants: async (
    sessionId: string,
  ): Promise<ParticipantListResponse> => {
    const response = await apiClient.get(
      `/studio/live-sessions/${sessionId}/participants`,
    );
    return response.data;
  },

  /**
   * Get all participants in a live session for a joined participant.
   *
   * Backed by ``GET /delivery/live-sessions/{session_id}/participants`` —
   * the same X-Participant-ID gating as the participant detail route.
   */
  getParticipantParticipants: async (
    sessionId: string,
    participantId: string,
  ): Promise<ParticipantListResponse> => {
    const config = addParticipantIdHeader({}, participantId);
    const response = await apiClient.get(
      `/delivery/live-sessions/${sessionId}/participants`,
      config,
    );
    return response.data;
  },

  /**
   * Remove a participant from a live session (educator only)
   * Completes all their sessions and broadcasts removal event
   */
  removeParticipant: async (
    sessionId: string,
    participantId: string
  ): Promise<{ success: boolean; display_name: string; sessions_completed: number; message: string }> => {
    const response = await apiClient.delete(`/live-sessions/${sessionId}/participants/${participantId}`);
    return response.data;
  },

  /**
   * Update participant heartbeat (supports anonymous participants)
   *
   * The endpoint authorizes via the live-session participant credential
   * chain; the X-Participant-ID header is the credential. The body
   * carries the same participant_id so the route can also assert the
   * caller is updating their own row (educators/hosts bypass that body
   * check via the OWNER relation).
   */
  updateHeartbeat: async (
    sessionId: string,
    data: HeartbeatRequest
  ): Promise<HeartbeatResponse> => {
    const config = addParticipantIdHeader({}, data.participant_id);
    const response = await anonymousClient.post(
      `/live-sessions/${sessionId}/heartbeat`,
      data,
      config,
    );
    return response.data;
  },

  // ============================================
  // PROGRESS
  // ============================================

  /**
   * Get aggregated progress for all participants (educator only)
   */
  getProgress: async (sessionId: string): Promise<SessionProgress> => {
    const response = await apiClient.get(`/live-sessions/${sessionId}/progress`);
    return response.data;
  },

  // ============================================
  // SESSION REUSE PREVIEW
  // ============================================

  /**
   * Get class context preview (existing assignments, sessions, and active live sessions)
   *
   * Called when educator opens the live session modal (without class) or selects a class.
   *
   * @param contentType - 'activity' or 'lesson'
   * @param contentId - The activity_id or lesson_id
   * @param classId - Optional class ID. Omit for no-class (anonymous) session context.
   *
   * Returns:
   * - active_live_session: If educator has an active/paused/lobby session for this content+class
   * - Assignment info (if class provided)
   * - Student session counts (if class provided)
   */
  getClassContext: async (params: {
    contentType: 'activity' | 'lesson';
    contentId: string;
    classId?: string;  // Optional - omit for no-class context
  }): Promise<ClassContextPreviewResponse> => {
    const searchParams = new URLSearchParams({
      content_type: params.contentType,
      content_id: params.contentId
    });
    // Only add class_id if provided
    if (params.classId) {
      searchParams.append('class_id', params.classId);
    }
    const response = await apiClient.get(`/live-sessions/context?${searchParams.toString()}`);
    return response.data;
  },

  // Deprecated: Use getClassContext instead
  getReusePreview: async (params: {
    contentType: 'activity' | 'lesson';
    contentId: string;
    classId?: string;
  }): Promise<ClassContextPreviewResponse> => {
    return liveSessionsApi.getClassContext(params);
  },

  // ============================================
  // GROUPS (Future Phase)
  // ============================================

};

// ============================================
// PUSHER CONFIGURATION API
// ============================================

export const pusherApi = {
  /**
   * Get Pusher configuration for frontend initialization
   */
  getConfig: async (): Promise<PusherConfig> => {
    const response = await apiClient.get('/pusher/config');
    return response.data;
  }
};

export default liveSessionsApi;
